import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Database, Download, RefreshCw, FlaskConical } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const fmtSize = (b) => (b ? `${(b / 1024 / 1024).toFixed(2)} MB` : '—');
const STATUS = {
    completed: ['Completado', 'bg-emerald-500/10 text-emerald-500'],
    running: ['En curso', 'bg-sky-500/10 text-sky-500'],
    failed: ['Falló', 'bg-red-500/10 text-red-500'],
};

export const BackupsCard = () => {
    const { apiCall, token } = useAdminAuth();
    const [data, setData] = useState({ items: [] });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try { setData((await apiCall('get', '/admin/backups')).data); } catch { /* staff sin permiso */ }
    }, [apiCall]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!data.items.some((b) => b.status === 'running' || b.restore_test?.status === 'running')) return undefined;
        const t = setTimeout(load, 4000);
        return () => clearTimeout(t);
    }, [data, load]);

    const runNow = async () => {
        setBusy(true);
        try { await apiCall('post', '/admin/backups/run'); toast.success('Respaldo iniciado'); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar el respaldo'); }
        finally { setBusy(false); }
    };

    const restoreTest = async (b) => {
        try {
            await apiCall('post', `/admin/backups/${b.id}/restore-test`);
            toast.success('Prueba de restauración iniciada en una base temporal');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar la prueba'); }
    };

    const download = async (b) => {
        try {
            const res = await fetch(`${API}/admin/backups/${b.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error();
            const url = URL.createObjectURL(await res.blob());
            const a = Object.assign(document.createElement('a'), { href: url, download: `aeroops-backup-${b.created_at.slice(0, 10)}.json.gz` });
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error('No se pudo descargar el respaldo'); }
    };

    return (
        <Card data-testid="backups-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                    <CardTitle className="text-lg flex items-center gap-2"><Database className="w-5 h-5 text-amber-500" />Respaldos de base de datos</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1" data-testid="backups-last-date">
                        Automático diario 3:00 a.m. · Último exitoso: {data.last_completed_at ? fmtDate(data.last_completed_at) : 'ninguno aún'}
                    </p>
                </div>
                <Button size="sm" onClick={runNow} disabled={busy} data-testid="backups-run-now-btn">
                    <RefreshCw className={`w-4 h-4 mr-1 ${busy ? 'animate-spin' : ''}`} />Respaldar ahora
                </Button>
            </CardHeader>
            <CardContent>
                <div className="max-h-72 overflow-y-auto divide-y divide-border" data-testid="backups-list">
                    {data.items.map((b) => {
                        const [label, cls] = STATUS[b.status] || [b.status, ''];
                        return (
                            <div key={b.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`backup-row-${b.id}`}>
                                <div className="min-w-0">
                                    <p className="font-medium">{fmtDate(b.created_at)}</p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {b.trigger === 'manual' ? `Manual · ${b.triggered_by || ''}` : 'Programado'} · {fmtSize(b.size_bytes)}{b.documents != null ? ` · ${b.documents} registros` : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {b.restore_test && (
                                        <Badge className={b.restore_test.status === 'passed' ? 'bg-emerald-500/10 text-emerald-500' : b.restore_test.status === 'running' ? 'bg-sky-500/10 text-sky-500' : 'bg-red-500/10 text-red-500'} data-testid={`backup-restore-status-${b.id}`}>
                                            {b.restore_test.status === 'passed' ? 'Restauración OK' : b.restore_test.status === 'running' ? 'Probando…' : 'Restauración falló'}
                                        </Badge>
                                    )}
                                    <Badge className={cls} data-testid={`backup-status-${b.id}`}>{label}</Badge>
                                    {b.status === 'completed' && (
                                        <Button size="icon" variant="ghost" title="Probar restauración" onClick={() => restoreTest(b)} disabled={b.restore_test?.status === 'running'} data-testid={`backup-restore-test-${b.id}`}><FlaskConical className="w-4 h-4" /></Button>
                                    )}
                                    {b.status === 'completed' && (
                                        <Button size="icon" variant="ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`}><Download className="w-4 h-4" /></Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    {data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aún no hay respaldos</p>}
                </div>
            </CardContent>
        </Card>
    );
};
